import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { INavLink, Nav } from '@fluentui/react';
import { IWithErrorHandlingProps } from './withErrorHandling';
import { OrbUserContext } from './OrbUserContext';
import { ThemeProvider } from '@fluentui/react/lib/Theme';
import { darkBlueTheme } from '../AppGlobals';
import { LoggedUserInfoForMenu } from './LoggedUserInfoForMenu';
import { DataContext } from './DataContext';
import { _onRenderNavLink } from './ZedXMainMenu';
import { TruncatedText } from './TruncatedText';
import { IEntityDescription1 } from '../types';

interface IKnowledgeHubMenu extends IWithErrorHandlingProps {
    onLinkClicked?: () => void;
    entityDescriptionForMenu?: IEntityDescription1;
}

export const KnowledgeHubMenu = ({
    entityDescriptionForMenu,
    errorHandling: { clearErrors },
    onLinkClicked
}: IKnowledgeHubMenu): React.ReactElement => {
    const isContextualMenu = false; // set true to show entity description
    const imagesRoot: string = process.env.REACT_APP_ZedXImagesRoot!;
    const { userPermissions: up } = useContext(OrbUserContext);
    const history = useHistory();
    const { pathname } = useLocation();
    const { dataServices } = useContext(DataContext);
    const [navs, setNavs] = useState([]);
    const resetErrors = useCallback(clearErrors, [clearErrors]);
    const [totalUpdates, setTotalUpdates] = useState(0);

    useEffect(() => {
        const routerNav = (path: string): void => {
            history.push(path);
            if (onLinkClicked) onLinkClicked();
        };

        const NamePathToNavLink = ({ name, path }): INavLink => ({
            key: path,
            name,
            url: `#${path}`,
            onClick: () => routerNav(path)
        });

        const links: INavLink[] = [
            { name: 'Home', path: '/' },
            { name: 'Welcome', path: '/a11/welcome' },
            { name: 'Insights', path: '/a11/insights' }
        ].map(NamePathToNavLink);

        setNavs(links);
    }, [up, history, onLinkClicked, resetErrors]);

    const selectedKey = pathname;

    return (
        <ThemeProvider theme={darkBlueTheme}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>

                {/* Logo */}
                <div
                    style={{
                        width: '100%',
                        paddingTop: '0',
                        paddingBottom: '1px',
                        paddingLeft: '15px',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <img
                        src={`${imagesRoot}/leftnav/LeftMenu-KnowledgeHub.png`}
                        alt="Knowledge Hub"
                        style={{ maxWidth: '100%', height: 'auto' }}
                    />
                </div>

                {/* Logged-in user */}
                <LoggedUserInfoForMenu />

                {/* Contextual header info */}
                {isContextualMenu && entityDescriptionForMenu && (
                    <div
                        style={{
                            backgroundColor: '#1A619F',
                            color: 'white',
                            padding: '10px 15px',
                            marginTop: '5px',
                            marginBottom: '10px',
                            marginLeft: '10px',
                            marginRight: '10px',
                            borderRadius: '4px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px'
                        }}
                    >
                        <TruncatedText text={entityDescriptionForMenu.DescriptionLine1} />
                        <TruncatedText text={entityDescriptionForMenu.DescriptionLine2} />
                    </div>
                )}

                {/* Navigation */}
                <Nav
                    selectedKey={selectedKey}
                    groups={[{ links: navs }]}
                    onRenderLink={(lnk) => _onRenderNavLink(lnk, totalUpdates)}
                />
            </div>
        </ThemeProvider>
    );
};
